/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ArrowLeftOutlined,
  LoadingOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import {
  Checkbox,
  Col,
  Form,
  Input,
  Row,
  Grid,
  Card,
  message,
  Tooltip,
  DatePicker,
} from 'antd';
import {
  Aviso,
  Button,
  TerminosYCondiciones,
  Text,
  Title,
} from 'components';
import LadaNumero from 'components/LadaNumero';
import Select from 'components/Select';
import { titleize, toUpper, toUpperFromConfig } from 'utils/formatters';
import { getRegimenesCapitales, getTiposDePersonas } from 'api/catalogos';
import _logger from 'utils/logger';
import {
  postCiudadano,
  postContribuyente,
} from 'api/padrones';
// SE QUITO PHONE_NUMBER PORQUE EN EL UNIQUEVALIDATOR YA VIENE ESA REGLA Y HACE REDUNDANCIA
import { CURP_VALIDATOR, RFC_VALIDATOR, NO_PLUS_EMAIL_RULE } from 'utils/patterns';
import { useLocation } from 'react-router';
import { padronesEnum } from 'views/Tramites/GPM/enums';
import { getEstadosCiviles, getGeneros } from 'api/usuarios';
import AvatarUploader from 'components/AvatarUploader';
import { getVerificarContribuyente } from 'api/empresas';
import { Turnstile } from '@marsidev/react-turnstile';
import { siteKey } from '../../../utils/env';
// import { ENV_NAME, ENV_NAMES_ENUM } from 'utils/env';

const trueFalseOptions = [
  { descripcion: 'Si', id: true },
  { descripcion: 'No', id: false },
];

const ValidatorLoading = () => (
  <Tooltip title="Validando información">
    <LoadingOutlined />
  </Tooltip>
);

const CURP = ({
  isCiudadano,
  foreign,
  tipoDePersona,
  rules,
  loading,
  disabled,
  loadingCURP,
  order,
}) => (((isCiudadano && !foreign) || (!isCiudadano && tipoDePersona !== 2))) && (
  <Col xs={24} sm={24} md={24} lg={8} order={order || (isCiudadano ? 1 : 2)}>
    <Form.Item
      label="CURP"
      name={isCiudadano ? 'CURP' : 'curp'}
      rules={rules.curp}
      normalize={toUpper}
      validateTrigger={['onBlur', 'onChange']}
    >
      <Input
        maxLength={18}
        disabled={loading || foreign || disabled}
        suffix={loadingCURP ? <ValidatorLoading /> : null}
      />
    </Form.Item>
  </Col>
);

const RFC = ({
  rules,
  tipoDePersona,
  loading,
  disabled,
  order,
  loadingRFC,
}) => (
  <Col xs={24} sm={24} md={24} lg={8} order={order}>
    <Form.Item
      label="RFC"
      name="rfc"
      rules={rules.rfc}
      normalize={toUpper}
    >
      <Input
        maxLength={tipoDePersona === 2 ? 12 : 13}
        disabled={loading || disabled}
        suffix={loadingRFC ? <ValidatorLoading /> : null}
      />
    </Form.Item>
  </Col>
);

const INE = ({
  isCiudadano,
  rules,
  tipoDePersona,
  loading,
  disabled,
  order,
}) => isCiudadano && (
  <Col xs={24} sm={24} md={24} lg={8} order={order}>
    <Form.Item
      label="INE"
      name="ine"
      rules={rules.ine}
      normalize={toUpper}
    >
      <Input maxLength={tipoDePersona === 2 ? 12 : 13} disabled={loading || disabled} />
    </Form.Item>
  </Col>
);

const FechaDeNacimiento = ({
  loading,
  disabled,
  order,
}) => (
  <Col xs={24} sm={24} md={24} lg={8} order={order}>
    <Form.Item
      name="fecha_nacimiento"
      label="Fecha de nacimiento"
      normalize={toUpper}
    >
      <DatePicker placeholder="" disabled={loading || disabled} />
    </Form.Item>
  </Col>
);

const isDisabled = (meta, _disabled) => {
  if (typeof meta === 'boolean') return meta;
  if (typeof meta === 'function') return meta();
  return _disabled;
};

const NumeroTelefono = ({
  loading,
  disabled,
  order,
  loadingCelular,
  ladaFormItemName,
  numberFormItemName,
  form,
  rules,
  meta,
}) => (
  <LadaNumero
    form={form}
    names={{
      lada: ladaFormItemName,
      numero_de_telefono: numberFormItemName,
    }}
    order={order}
    rules={rules}
    suffix={loadingCelular ? <ValidatorLoading /> : null}
    disabled={isDisabled(meta?.telefono?.disabled, loadingCelular || loading || disabled)}
    lg={8}
  />
);

const CorreoElectronico = ({
  isCiudadano,
  loading,
  disabled,
  order,
  loadingEmail,
  rules,
  meta,
}) => (
  <Col
    xs={24}
    sm={24}
    md={24}
    lg={8}
    order={order}
  >
    <Form.Item
      label="Correo electrónico"
      name={isCiudadano ? 'email' : 'correo_electronico'}
      rules={rules.email}
      validateTrigger={['onBlur', 'onChange']}
      normalize={(v) => toUpperFromConfig(v)}
    >
      <Input
        suffix={loadingEmail ? (<ValidatorLoading />) : null}
        disabled={isDisabled(meta?.email?.disabled, loadingEmail || loading || disabled)}
      />
    </Form.Item>
  </Col>
);

const EsExtranjero = ({
  rules,
  disabled,
  order,
}) => (
  <Col xs={24} sm={24} md={24} lg={8} order={order}>
    <Form.Item
      label="¿Es extranjero?"
      name="es_extranjero"
      rules={rules.required}
    >
      <Select disabled={disabled} options={trueFalseOptions} allowClear={false} />
    </Form.Item>
  </Col>
);

const TipoDePersona = ({
  isCiudadano,
  order,
  disabled,
  rules,
  tiposDePersonas,
}) => !isCiudadano && (
  <Col xs={24} sm={24} md={24} lg={8} order={order}>
    <Form.Item
      label="Tipo de persona"
      name="tipo_de_persona"
      rules={rules.required}
    >
      <Select
        disabled={disabled}
        options={tiposDePersonas}
        allowClear={false}
      />
    </Form.Item>
  </Col>
);

const NombreCompleto = ({
  rules,
  disabled,
  isCiudadano,
  generos,
  estadosCiviles,
}) => (
  <>
    <Col xs={24} sm={24} md={24} lg={8} order={8}>
      <Form.Item
        label="Nombre(s)"
        name="nombre"
        rules={rules.required}
        normalize={(v) => toUpperFromConfig(v, titleize)}
      >
        <Input disabled={disabled} />
      </Form.Item>
    </Col>
    <Col xs={24} sm={24} md={12} lg={8} order={9}>
      <Form.Item
        label="Apellido Paterno"
        name="apellido_paterno"
        rules={rules.required}
        normalize={(v) => toUpperFromConfig(v, titleize)}
      >
        <Input disabled={disabled} />
      </Form.Item>
    </Col>
    <Col xs={24} sm={24} md={12} lg={8} order={10}>
      <Form.Item
        label="Apellido Materno"
        name="apellido_materno"
        normalize={(v) => toUpperFromConfig(v, titleize)}
      >
        <Input disabled={disabled} />
      </Form.Item>
    </Col>
    {isCiudadano && (
      <>
        <Col md={8} sm={24} xs={24} order={8}>
          <Form.Item
            label="Género"
            name="genero"
          >
            <Select options={generos} disabled={disabled} />
          </Form.Item>
        </Col>
        <Col md={8} sm={24} xs={24} order={9}>
          <Form.Item
            label="Estado civil"
            name="estado_civil"
          >
            <Select options={estadosCiviles} disabled={disabled} />
          </Form.Item>
        </Col>
      </>
    )}
  </>
);

const RegimenRazonSocial = ({
  rules,
  disabled,
  regimenesCapitales,
}) => (
  <>
    <Col xs={24} sm={24} md={24} lg={8} order={8}>
      <Form.Item
        label="Régimen Capital"
        name="regimen_capital"
        rules={rules.required}
      >
        <Select
          disabled={disabled}
          options={regimenesCapitales}
          getLabel={(e) => e.descripcion.concat(` (${e.abreviatura})`)}
        />
      </Form.Item>
    </Col>
    <Col xs={24} sm={24} md={24} lg={16} xl={8} order={9}>
      <Form.Item
        label="Razón Social"
        name="razon_social"
        tooltip="No incluir las siglas de régimen capital"
        rules={rules.required}
        normalize={(v) => toUpperFromConfig(v, titleize)}
      >
        <Input disabled={disabled} />
      </Form.Item>
    </Col>
  </>
);

const General = ({
  disabled,
  inAppointments,
  inMisDatos,
  isCiudadano,
  rules,
  tiposDePersonas,
  tipoDePersona,
  estadosCiviles,
  generos,
  regimenesCapitales,
  meta,
}) => (
  <>
    {!inAppointments && !inMisDatos && isCiudadano && (
      <EsExtranjero order={6} disabled={disabled} rules={rules} meta={meta} />
    )}
    {inMisDatos && (
      <TipoDePersona
        isCiudadano={isCiudadano}
        disabled={disabled}
        order={7}
        rules={rules}
        tiposDePersonas={tiposDePersonas}
        meta={meta}
      />
    )}
    {(isCiudadano || tipoDePersona !== 2) ? (
      <NombreCompleto
        rules={rules}
        disabled={disabled}
        isCiudadano={isCiudadano}
        generos={generos}
        estadosCiviles={estadosCiviles}
        meta={meta}
      />
    ) : (
      <RegimenRazonSocial
        rules={rules}
        disabled={disabled}
        regimenesCapitales={regimenesCapitales}
        meta={meta}
      />
    )}
    {inMisDatos && tipoDePersona === 2 && (
      <Col xs={24} sm={24} md={8} order={11}>
        <Form.Item
          name="nombre_comercial"
          label="Nombre comercial"
        >
          <Input disabled={disabled} />
        </Form.Item>
      </Col>
    )}
  </>
);

const Identidad = ({
  isCiudadano,
  form,
  disabled,
  rules,
  tipoDePersona,
  loading,
  loadingRFC,
  foreign,
  loadingCURP,
  tiposDePersonas,
  inMisDatos,
  meta,
}) => (
  <>
    {isCiudadano && (
      <Col xs={24} sm={24} md={8} order={1}>
        <AvatarUploader
          form={form}
          imageURL={form.getFieldValue('foto')}
          disabled={disabled}
        />
      </Col>
    )}
    <TipoDePersona
      disabled={inMisDatos || loading || disabled}
      order={1}
      rules={rules}
      isCiudadano={isCiudadano}
      tiposDePersonas={tiposDePersonas}
      meta={meta}
    />
    <RFC
      rules={rules}
      tipoDePersona={tipoDePersona}
      loading={loading}
      disabled={disabled}
      order={2}
      loadingRFC={loadingRFC}
      meta={meta}
    />
    <CURP
      isCiudadano={isCiudadano}
      foreign={foreign}
      tipoDePersona={tipoDePersona}
      rules={rules}
      loading={loading}
      disabled={disabled}
      loadingCURP={loadingCURP}
      order={3}
      meta={meta}
    />
    <INE
      isCiudadano={isCiudadano}
      rules={rules}
      tipoDePersona={tipoDePersona}
      loading={loading}
      disabled={disabled}
      order={4}
      meta={meta}
    />
    {isCiudadano && (
      <FechaDeNacimiento
        isCiudadano={isCiudadano}
        loading={loading}
        disabled={disabled}
        order={5}
        meta={meta}
      />
    )}
  </>
);

const Contacto = ({
  loading,
  disabled,
  loadingCelular,
  ladaFormItemName,
  numberFormItemName,
  form,
  rules,
  loadingEmail,
  isCiudadano,
  meta,
}) => (
  <>
    <NumeroTelefono
      loading={loading}
      disabled={disabled}
      order={11}
      loadingCelular={loadingCelular}
      ladaFormItemName={ladaFormItemName}
      numberFormItemName={numberFormItemName}
      form={form}
      rules={rules}
      meta={meta}
    />
    <CorreoElectronico
      loading={loading}
      disabled={disabled}
      order={12}
      loadingCelular={loadingEmail}
      rules={rules}
      isCiudadano={isCiudadano}
      meta={meta}
    />
  </>
);

const FormItems = ({
  rules,
  loading,
  foreign,
  loadingCURP,
  tiposDePersonas,
  tipoDePersona,
  regimenesCapitales,
  form,
  ladaFormItemName,
  numberFormItemName,
  loadingCelular,
  loadingEmail,
  disabled,
  isCiudadano,
  generos,
  estadosCiviles,
  loadingRFC,
  inAppointments,
  inMisDatos,
  meta,
}) => (
  <>
    <Identidad
      isCiudadano={isCiudadano}
      form={form}
      disabled={disabled}
      rules={rules}
      tipoDePersona={tipoDePersona}
      loading={loading}
      loadingRFC={loadingRFC}
      foreign={foreign}
      loadingCURP={loadingCURP}
      tiposDePersonas={tiposDePersonas}
      inMisDatos={inMisDatos}
      inAppointments={inAppointments}
      meta={meta}
    />
    <General
      disabled={loading || disabled}
      inMisDatos={inMisDatos}
      inAppointments={inAppointments}
      isCiudadano={isCiudadano}
      rules={rules}
      tiposDePersonas={tiposDePersonas}
      tipoDePersona={tipoDePersona}
      estadosCiviles={estadosCiviles}
      generos={generos}
      regimenesCapitales={regimenesCapitales}
      meta={meta}
    />
    <Contacto
      loading={loading}
      disabled={disabled}
      loadingCelular={loadingCelular}
      ladaFormItemName={ladaFormItemName}
      numberFormItemName={numberFormItemName}
      form={form}
      rules={rules}
      loadingEmail={loadingEmail}
      isCiudadano={isCiudadano}
      meta={meta}
    />
    {/* {isCiudadano && (
      <Col xs={24} sm={24} md={8} order={9}>
        <Form.Item
          name="email_alternativo"
          rules={rules.email_alternativo}
          label="Correo alternativo"
          normalize={(v) => toUpperFromConfig(v)}
        >
          <Input allowClear disabled={loading || disabled} />
        </Form.Item>
      </Col>
    )} */}
    <Form.Item hidden>
      <Button htmlType="submit" />
    </Form.Item>
  </>
);

const RegisterPadron = ({
  callback,
  tipoDePadron,
  onCancel,
  onlyItems,
  rulesRFC,
  rulesCURP,
  disabled,
  selfId,
  general,
  identidad,
  contacto,
  uniqueEmail,
  uniqueCellphone,
  meta,
  padron,
}) => {
  const screens = Grid.useBreakpoint();
  const { pathname } = useLocation();
  const form = onlyItems ? Form.useFormInstance() : Form.useForm()[0];
  const isCiudadano = tipoDePadron === padronesEnum.CIUDADANO;
  const ladaFormItemName = isCiudadano ? 'lada' : 'lada_celular';
  const numberFormItemName = isCiudadano ? 'numero_de_celular' : 'telefono_celular';
  const tipoDePersona = Form.useWatch('tipo_de_persona', form);
  const foreign = Form.useWatch('es_extranjero', form);
  const [loading, setLoading] = useState(false);
  const [tiposDePersonas, setTiposDePersonas] = useState([]);
  const [regimenesCapitales, setRegimenesCapitales] = useState([]);
  const [acceptedTermns, setAcceptedTermns] = useState(false);
  const [loadingCURP, setLoadingCURP] = useState(false);
  const [loadingRFC, setLoadingRFC] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [loadingCelular, setLoadingCelular] = useState(false);
  const [generos, setGeneros] = useState([]);
  const [estadosCiviles, setEstadosCiviles] = useState([]);
  const [token, setToken] = useState();

  const padronString = isCiudadano ? 'Ciudadano' : 'Contribuyente';

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const promises = [
        getTiposDePersonas(),
        getRegimenesCapitales(),
      ];
      if (general) {
        promises.push(getGeneros());
        promises.push(getEstadosCiviles());
      }
      const [
        _tiposDePersonas,
        _regimenesCapitales,
        _generos = [],
        _estadosCiviles = [],
      ] = await Promise.all(promises);
      setGeneros(_generos);
      setEstadosCiviles(_estadosCiviles);
      setTiposDePersonas(_tiposDePersonas);
      setRegimenesCapitales(_regimenesCapitales);
      setLoading(false);
    };
    fetch();
  }, []);

  const onFinish = async () => {
    try {
      setLoading(true);
      const values = form.getFieldsValue();
      values.cf_turnstile_token = token;
      if (isCiudadano || tipoDePersona === 1) {
        values.razon_social = `${values.nombre} ${values.apellido_paterno} ${values.apellido_materno || ''}`.trim();
      }
      if (!acceptedTermns) {
        message.warn('Debe aceptar el aviso de privacidad y los términos de uso', 2.5);
        setLoading(false);
        return;
      }
      await form.validateFields();
      const registered = await (isCiudadano
        ? postCiudadano : postContribuyente)(values);
      if (registered) {
        setLoading(false);
        await callback(registered);
      }
      setLoading(false);
    } catch (err) {
      _logger(err);
      setLoading(false);
    }
  };

  const uniqueValidator = {
    validator: async ({ field }) => {
      const values = form.getFieldsValue();
      const emailField = isCiudadano ? 'email' : 'correo_electronico';
      if (field === emailField) {
        const email = values[emailField];
        if (!email || padron?.[emailField] === email) return;
        setLoadingEmail(true);
        const exist = await getVerificarContribuyente({ correo: email });
        setLoadingEmail(false);
        if (exist && exist.id !== selfId) {
          throw new Error(`Ya existe un ${isCiudadano ? 'ciudadano' : 'contribuyente'} con este correo electrónico`);
        }
        return;
      }
      if (field === numberFormItemName) {
        const numero = values[numberFormItemName];
        if (!numero || padron?.[numberFormItemName] === numero) return;
        if (numero?.toString()?.length !== 10) {
          throw new Error('Ingrese un número celular correcto');
        }
        setLoadingCelular(true);
        const exist = await getVerificarContribuyente({ telefono: numero });
        setLoadingCelular(false);
        if (exist && exist.id !== selfId) {
          throw new Error(`Ya existe un ${isCiudadano ? 'ciudadano' : 'contribuyente'} con este número de celular`);
        }
        return;
      }
      const curpField = isCiudadano ? 'CURP' : 'curp';
      if (field === curpField) {
        const curp = values[curpField];
        if (!curp || padron?.[curpField] === curp) return;
        await CURP_VALIDATOR(tipoDePersona || 1).validator(field, curp);
        setLoadingCURP(true);
        const exist = await getVerificarContribuyente({ [curpField]: curp });
        setLoadingCURP(false);
        if (exist && exist.id !== selfId) {
          throw new Error(`Ya existe un ${isCiudadano ? 'ciudadano' : 'contribuyente'} con esta CURP`);
        }
        return;
      }
      if (field === 'rfc') {
        if (!values.rfc || padron?.rfc === values.rfc) return;
        await RFC_VALIDATOR(tipoDePersona || 1).validator(field, values.rfc);
        setLoadingRFC(true);
        // el ciudadano espera mayusculas
        const rfcKey = isCiudadano ? 'RFC' : 'rfc';
        const exist = await getVerificarContribuyente({ [rfcKey]: values.rfc });
        setLoadingRFC(false);
        if (exist && exist.id !== selfId) {
          throw new Error(`Ya existe un ${isCiudadano ? 'ciudadano' : 'contribuyente'} con este RFC`);
        }
      }
    },
  };

  const inMisDatos = pathname.includes('mi-cuenta/mis-datos');
  const inAppointments = pathname.includes('citas');

  const requiredRule = { required: true };

  const rules = {
    required: [requiredRule],
    email: [
      requiredRule,
      { type: 'email' },
      NO_PLUS_EMAIL_RULE,
      uniqueEmail && uniqueValidator,
    ].filter(Boolean),
    email_alternativo: [
      requiredRule,
      { type: 'email' },
      NO_PLUS_EMAIL_RULE,
    ],
    [numberFormItemName]: [
      requiredRule,
      uniqueCellphone && uniqueValidator,
      // Aqui se quito el phone number porque hacia redundancia
    ].filter(Boolean),
    lada: [
      {
        ...requiredRule,
        message: 'Requerido',
      },
      uniqueValidator,
    ],
    curp: rulesCURP || [
      { required: isCiudadano && !foreign },
      uniqueValidator,
    ],
    rfc: rulesRFC || [
      !isCiudadano && requiredRule,
      uniqueValidator,
    ].filter(Boolean),
  };

  if (onlyItems) {
    if (contacto) {
      return (
        <>
          <Contacto
            loading={loading}
            disabled={disabled}
            loadingCelular={loadingCelular}
            ladaFormItemName={ladaFormItemName}
            numberFormItemName={numberFormItemName}
            form={form}
            rules={rules}
            loadingEmail={loadingEmail}
            isCiudadano={isCiudadano}
            meta={meta}
          />
          <Form.Item hidden>
            <Button htmlType="submit" />
          </Form.Item>
        </>
      );
    }

    if (identidad) {
      return (
        <>
          <Identidad
            isCiudadano={isCiudadano}
            form={form}
            disabled={disabled}
            rules={rules}
            tipoDePersona={tipoDePersona}
            loading={loading}
            loadingRFC={loadingRFC}
            foreign={foreign}
            loadingCURP={loadingCURP}
            tiposDePersonas={tiposDePersonas}
            inMisDatos={inMisDatos}
            meta={meta}
          />
          <Form.Item hidden>
            <Button htmlType="submit" />
          </Form.Item>
        </>
      );
    }

    if (general) {
      return (
        <>
          <General
            disabled={loading || disabled}
            inAppointments={inAppointments}
            inMisDatos={inMisDatos}
            isCiudadano={isCiudadano}
            rules={rules}
            tiposDePersonas={tiposDePersonas}
            tipoDePersona={tipoDePersona}
            estadosCiviles={estadosCiviles}
            generos={generos}
            regimenesCapitales={regimenesCapitales}
            meta={meta}
          />
          <Form.Item hidden>
            <Button htmlType="submit" />
          </Form.Item>
        </>
      );
    }

    return (
      <FormItems
        inAppointments={inAppointments}
        inMisDatos={inMisDatos}
        rules={rules}
        loading={loading}
        foreign={foreign}
        loadingCURP={loadingCURP}
        tiposDePersonas={tiposDePersonas}
        tipoDePersona={tipoDePersona}
        regimenesCapitales={regimenesCapitales}
        form={form}
        numberFormItemName={numberFormItemName}
        ladaFormItemName={ladaFormItemName}
        loadingCelular={loadingCelular}
        loadingEmail={loadingEmail}
        disabled={disabled}
        isCiudadano={isCiudadano}
        generos={generos}
        estadosCiviles={estadosCiviles}
        loadingRFC={loadingRFC}
        meta={meta}
      />
    );
  }

  return (
    <Card
      title={(
        <Title level={2} $accent>
          Alta de
          {' '}
          {padronString}
        </Title>
      )}
      style={{
        marginLeft: 'auto',
        marginRight: 'auto',
        width: '100%',
      }}
      bordered
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        style={{ width: '100%' }}
        initialValues={{
          lada: 34,
          tipo_de_persona: 2,
          es_extranjero: false,
        }}
      >
        <Row gutter={10}>
          <FormItems
            inAppointments={inAppointments}
            inMisDatos={inMisDatos}
            rules={rules}
            loading={loading}
            foreign={foreign}
            loadingCURP={loadingCURP}
            tiposDePersonas={tiposDePersonas}
            tipoDePersona={tipoDePersona}
            regimenesCapitales={regimenesCapitales}
            form={form}
            numberFormItemName={numberFormItemName}
            ladaFormItemName={ladaFormItemName}
            loadingCelular={loadingCelular}
            loadingEmail={loadingEmail}
            isCiudadano={isCiudadano}
            generos={generos}
            estadosCiviles={estadosCiviles}
            loadingRFC={loadingRFC}
            meta={meta}
          />
          <Col span={24} order={13}>
            <Checkbox
              onChange={({ target: { checked } }) => setAcceptedTermns(checked)}
              checked={acceptedTermns}
              disabled={loading}
            >
              <Text>
                Conozco el
                {' '}
                <Aviso />
                {' '}
                y acepto los
                {' '}
                <TerminosYCondiciones />
                {' '}
                del portal
              </Text>
            </Checkbox>
          </Col>
        </Row>
        <br />
        <Row justify="space-between">
          <Col xs={24} sm={24} md={8} order={screens.md ? 1 : 2}>
            <Form.Item>
              <Button
                block={!screens.md}
                loading={loading}
                onClick={onCancel}
              >
                <ArrowLeftOutlined />
                Regresar
              </Button>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8} order={screens.md ? 2 : 1}>
            <Form.Item>
              <Button
                block={!screens.md}
                htmlType="submit"
                loading={loading}
                disabled={!token}
                style={{ float: 'right' }}
                $accent
              >
                Guardar
                <SaveOutlined />
              </Button>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item noStyle>
          <Turnstile
            siteKey={siteKey}
            onSuccess={(cf_turnstile_token) => setToken(cf_turnstile_token)}
            onError={() => setToken(null)}
            onExpire={() => setToken(null)}
          />
        </Form.Item>
      </Form>
    </Card>
  );
};

RegisterPadron.propTypes = {
  onCancel: PropTypes.func.isRequired,
  callback: PropTypes.func.isRequired,
  tipoDePadron: PropTypes.number.isRequired,
  onlyItems: PropTypes.bool,
  rulesRFC: PropTypes.arrayOf(PropTypes.shape({})),
  disabled: PropTypes.bool,
  selfId: PropTypes.number,
  general: PropTypes.bool,
  identidad: PropTypes.bool,
  contacto: PropTypes.bool,
  uniqueEmail: PropTypes.bool,
  uniqueCellphone: PropTypes.bool,
  meta: PropTypes.shape(),
  padron: PropTypes.shape(),
};

RegisterPadron.defaultProps = {
  onlyItems: false,
  disabled: false,
  rulesRFC: null,
  selfId: null,
  general: false,
  identidad: false,
  contacto: false,
  uniqueEmail: true,
  uniqueCellphone: true,
  meta: null,
  padron: null,
};

export default RegisterPadron;
